"use client";

import { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Button,
  SimpleGrid,
  useToast,
  RadioGroup,
  Radio,
  HStack,
  Alert,
  AlertIcon,
  AlertDescription,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Flex,
} from "@chakra-ui/react";

import { supabase } from "../supabase.ts";

import Sidebar from "./Sidebar.tsx";
import { useAppTheme } from "../ThemeContext.tsx";

const AccountPreferences = () => {
  const { theme, setTheme, resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState({
    email: "",
    firstName: "",
    lastName: "",
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) {
      setUserData({
        email: user.email || "",
        firstName: user.user_metadata?.first_name || "",
        lastName: user.user_metadata?.last_name || "",
      });
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          first_name: userData.firstName,
          last_name: userData.lastName,
        },
      });

      if (error) throw error;

      toast({
        title: "Profile updated successfully",
        status: "success",
        duration: 3000,
      });
    } catch (error: any) {
      toast({
        title: "Error updating profile",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user?.id) throw new Error("User ID not found");

      const { error } = await supabase.auth.admin.deleteUser(user.id);
      if (error) throw error;

      await supabase.auth.signOut();
      window.location.href = "/login";
    } catch (error: any) {
      toast({
        title: "Error deleting account",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  return (
    <Flex
      minHeight="100vh"
      // We can style the Flex based on theme:
      bg={isDark ? "gray.900" : "gray.100"}
      color={isDark ? "whiteAlpha.900" : "gray.800"}
    >
      <Sidebar />
      <Box flex="1" maxW="1200px" mx="auto" py={8} px={4}>
        <VStack spacing={8} align="stretch">
          <Box>
            <Heading size="lg" mb={2}>
              User Preferences
            </Heading>
            <Text color={isDark ? "gray.400" : "gray.600"}>
              Manage your profile, account settings, and preferences for your
              Concierge-AI experience
            </Text>
          </Box>

          {/* Account Information */}
          <Box
            // Dark/light box background:
            bg={isDark ? "gray.800" : "white"}
            rounded="lg"
            p={6}
            borderWidth="1px"
            borderColor={isDark ? "gray.700" : "gray.200"}
          >
            <Heading
              size="md"
              mb={4}
              color={isDark ? "whiteAlpha.900" : "gray.800"}
            >
              Account Information
            </Heading>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>First name</FormLabel>
                <Input
                  value={userData.firstName}
                  onChange={(e) =>
                    setUserData({ ...userData, firstName: e.target.value })
                  }
                  bg={isDark ? "gray.700" : "gray.50"}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Last name</FormLabel>
                <Input
                  value={userData.lastName}
                  onChange={(e) =>
                    setUserData({ ...userData, lastName: e.target.value })
                  }
                  bg={isDark ? "gray.700" : "gray.50"}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input
                  value={userData.email}
                  isReadOnly
                  bg={isDark ? "gray.700" : "gray.50"}
                  cursor="not-allowed"
                />
              </FormControl>
              <HStack spacing={2} alignSelf="flex-end">
                <Button
                  colorScheme={isDark ? "black" : "white"}
                  variant="ghost"
                  onClick={() => loadUserData()}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="gray"
                  onClick={handleSave}
                  isLoading={loading}
                >
                  Save
                </Button>
              </HStack>
            </VStack>
          </Box>

          {/* Appearance */}
          <Box
            bg={isDark ? "gray.800" : "white"}
            rounded="lg"
            p={6}
            borderWidth="1px"
            borderColor={isDark ? "gray.700" : "gray.200"}
          >
            <Heading
              size="md"
              mb={4}
              color={isDark ? "whiteAlpha.900" : "gray.800"}
            >
              Appearance
            </Heading>
            <Text color={isDark ? "gray.400" : "gray.600"} mb={4}>
              Choose how the application looks to you. Select a single theme, or
              sync with your system.
            </Text>
            <RadioGroup value={theme} onChange={setTheme}>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Box
                  p={4}
                  border="1px solid"
                  borderColor={
                    theme === "dark"
                      ? "gray.500"
                      : isDark
                      ? "gray.700"
                      : "gray.200"
                  }
                  rounded="md"
                  cursor="pointer"
                  onClick={() => setTheme("dark")}
                >
                  <Radio
                    value="dark"
                    sx={{
                      borderWidth: "2px",
                      borderColor: isDark ? "gray.500" : "gray.400",
                      borderRadius: "full",
                      width: "15px",
                      height: "15px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      _checked: {
                        bg: isDark ? "gray.700" : "gray.200",
                        borderColor: isDark ? "white" : "black",
                        position: "relative",
                        _after: {
                          content: '""',
                          position: "absolute",
                          width: "7px",
                          height: "7px",
                          bg: "gray.500",
                          borderRadius: "full",
                        },
                      },
                    }}
                  >
                    Dark
                  </Radio>
                </Box>
                <Box
                  p={4}
                  border="1px solid"
                  borderColor={
                    theme === "light"
                      ? "gray.500"
                      : isDark
                      ? "gray.700"
                      : "gray.200"
                  }
                  rounded="md"
                  cursor="pointer"
                  onClick={() => setTheme("light")}
                >
                  <Radio
                    value="light"
                    sx={{
                      borderWidth: "2px",
                      borderColor: isDark ? "gray.500" : "gray.400",
                      borderRadius: "full",
                      width: "15px",
                      height: "15px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      _checked: {
                        bg: isDark ? "gray.700" : "gray.200",
                        borderColor: isDark ? "white" : "black",
                        position: "relative",
                        _after: {
                          content: '""',
                          position: "absolute",
                          width: "7px",
                          height: "7px",
                          bg: "gray.500",
                          borderRadius: "full",
                        },
                      },
                    }}
                  >
                    Light
                  </Radio>
                </Box>
                <Box
                  p={4}
                  border="1px solid"
                  borderColor={
                    theme === "system"
                      ? "gray.500"
                      : isDark
                      ? "gray.700"
                      : "gray.200"
                  }
                  rounded="md"
                  cursor="pointer"
                  onClick={() => setTheme("system")}
                >
                  <Radio
                    value="system"
                    sx={{
                      borderWidth: "2px",
                      borderColor: isDark ? "gray.500" : "gray.400",
                      borderRadius: "full",
                      width: "15px",
                      height: "15px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      _checked: {
                        bg: isDark ? "gray.700" : "gray.200",
                        borderColor: isDark ? "white" : "black",
                        position: "relative",
                        _after: {
                          content: '""',
                          position: "absolute",
                          width: "7px",
                          height: "7px",
                          bg: "gray.500",
                          borderRadius: "full",
                        },
                      },
                    }}
                  >
                    System
                  </Radio>
                </Box>
              </SimpleGrid>
            </RadioGroup>
          </Box>

          {/* Danger Zone */}
          <Box
            bg={isDark ? "gray.800" : "white"}
            rounded="lg"
            p={6}
            borderWidth="1px"
            borderColor="red.900"
          >
            <Heading size="md" mb={4} color="red.400">
              DANGER ZONE
            </Heading>
            <Alert status="error" bg="red.900" rounded="md">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold" color="gray.100">
                  Request for account deletion
                </Text>
                <AlertDescription color="gray.100">
                  Deleting your account is permanent and cannot be undone. Your
                  data will be deleted within 30 days, except we may retain some
                  metadata and logs for longer where required or permitted by
                  law.
                </AlertDescription>
              </Box>
            </Alert>
            <Button
              mt={4}
              colorScheme="red"
              variant="outline"
              size="sm"
              onClick={onOpen}
            >
              Delete account
            </Button>
          </Box>
        </VStack>

        {/* Delete Account Confirmation Modal */}
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent
            // Same logic: if dark, use dark background
            bg={isDark ? "gray.800" : "white"}
            color={isDark ? "whiteAlpha.900" : "gray.800"}
          >
            <ModalHeader>Confirm Account Deletion</ModalHeader>
            <ModalBody>
              <Text>
                Are you sure you want to delete your account? This action cannot
                be undone.
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme={isDark ? "black" : "white"}
                variant="ghost"
                mr={3}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDeleteAccount}>
                Delete Account
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </Flex>
  );
};

export default AccountPreferences;
